"use client";

import { useState, useRef } from "react";
import { Image, ArrowUp, X } from "lucide-react";

const CommentInput = ({ onSubmit, isReply, replyingTo }) => {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async () => {
    if ((!text.trim() && !image) || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await onSubmit(text, image);
      setText("");
      setImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = null;
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = (text.trim() || image) && !isSubmitting;

  return (
    <div className="relative w-full">
      {replyingTo && isReply && (
        <div className="text-xs text-gray-500 mb-2 ml-2">
          Replying to @{replyingTo}
        </div>
      )}

      {imagePreview && (
        <div className="mb-3 relative">
          <img
            src={imagePreview}
            alt="Preview"
            className="w-full max-h-48 object-contain rounded-lg border"
          />
          <button
            className="absolute top-2 right-2 bg-gray-800 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-700 transition"
            onClick={removeImage}
            type="button"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="flex items-center bg-gray-100 rounded-full px-4 py-2 w-full overflow-hidden max-w-full">
        <input
          type="text"
          placeholder={isReply ? "Write a reply..." : "Leave a comment..."}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm"
          disabled={isSubmitting}
        />

        <div className="flex items-center space-x-2">
          <button
            className="text-gray-500 p-2 rounded-full hover:bg-gray-200 transition disabled:opacity-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
            type="button"
          >
            <Image size={20} />
          </button>

          <button
            className={`bg-black text-white p-2 rounded-full flex items-center justify-center w-9 h-9 transition ${
              canSubmit ? "hover:bg-gray-800" : "opacity-50 cursor-not-allowed"
            }`}
            onClick={handleSubmit}
            disabled={!canSubmit}
            type="button"
          >
            <ArrowUp size={20} />
          </button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageSelect}
          accept="image/*"
          className="hidden"
        />
      </div>
    </div>
  );
};

export default CommentInput;
